import React, { useState, useCallback, useMemo } from 'react';
import { Select, Spin, DatePicker, InputNumber, Input, Upload, Button, Table, Card,
         Row, Col, Statistic, Icon, message, Alert } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import numeral from 'numeral';
import debounce from 'lodash/debounce';
import api from 'api';
import ErrorMessage from 'components/ErrorMessage';

const { Option } = Select;

// QuickBooks-style "Receive Payment" screen. Intercepts /transactions/new (see routes.js).
// Flow: pick a customer -> their open invoices load -> enter payment amount / type / date /
// attachment -> check invoice lines to apply the payment (each line's amount is quick-editable
// for partials) -> Save & Close (back to the Payments list) or Save & New (clear for another).
//
// One POST /transactions carries the transaction fields plus an `applications` array
// [{invoice_id, amount}]; the backend mints the transaction and its invoice_payments atomically
// (helpers/hooks/stashPaymentApplications + applyPaymentApplications). Any unapplied remainder
// stays as available credit on the transaction, re-appliable later.

const TYPE_OPTIONS = [
    { value: 'check',       label: 'Check' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'ach',         label: 'ACH' },
    { value: 'wire',        label: 'Wire' },
];

const money = (n) => `$${numeral(Number(n || 0)).format('0,0.00')}`;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

function ReceivePayment({ history }) {
    // customer
    const [customerId, setCustomerId] = useState(null);
    const [customerName, setCustomerName] = useState('');
    const [customerOptions, setCustomerOptions] = useState([]);
    const [searching, setSearching] = useState(false);

    // open invoices for the selected customer
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    // applied amounts keyed by invoice id (a key present == that line is applied/checked)
    const [applied, setApplied] = useState({});

    // payment header
    const [paymentAmount, setPaymentAmount] = useState(null);
    const [amountTouched, setAmountTouched] = useState(false);
    const [type, setType] = useState('check');
    const [checkNumber, setCheckNumber] = useState('');
    const [lastDigits, setLastDigits] = useState('');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [paymentDate, setPaymentDate] = useState(moment());
    const [memo, setMemo] = useState('');
    const [file, setFile] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const appliedTotal = useMemo(
        () => round2(Object.values(applied).reduce((s, v) => s + Number(v || 0), 0)),
        [applied]);

    // With no manually-typed amount, the payment amount follows what's applied so clicking
    // lines fills the amount for you. Once the user edits the amount box, respect it verbatim.
    const effectivePayment = amountTouched ? Number(paymentAmount || 0) : appliedTotal;
    const unapplied = round2(effectivePayment - appliedTotal);

    // ---- customer search ----------------------------------------------------
    const doSearch = useCallback((text) => {
        if (!text) { setCustomerOptions([]); return; }
        setSearching(true);
        api.get('/customers', { query: text, per_page: 8, fields: 'id,name' })
            .then((res) => {
                setSearching(false);
                setCustomerOptions((res && res.body) || []);
            })
            .catch(() => setSearching(false));
    }, []);
    const onCustomerSearch = useMemo(() => debounce(doSearch, 300), [doSearch]);

    const loadInvoices = useCallback(async (id) => {
        setLoadingInvoices(true);
        setApplied({});
        try {
            const res = await api.get('/lists/customer_open_invoices', { customer_id: id });
            setInvoices((res && res.body) || []);
        } catch (e) {
            setInvoices([]);
        } finally {
            setLoadingInvoices(false);
        }
    }, []);

    const selectCustomer = (id) => {
        const found = customerOptions.find((c) => c.id === id);
        setCustomerId(id);
        setCustomerName(found ? found.name : '');
        setError(null);
        if (id) loadInvoices(id);
        else { setInvoices([]); setApplied({}); }
    };

    // ---- applying to invoice lines -----------------------------------------
    const setLineAmount = (invoiceId, value) => {
        setApplied((prev) => ({ ...prev, [invoiceId]: value }));
    };

    const toggleLine = (invoice, checked) => {
        setApplied((prev) => {
            const next = { ...prev };
            if (checked) {
                const openBal = Number(invoice.open_balance || 0);
                // When an amount is set, only fill up to what's still unapplied; otherwise fill
                // the whole open balance (the amount box will follow along).
                if (amountTouched) {
                    const otherApplied = Object.entries(prev)
                        .reduce((s, [k, v]) => s + (k === invoice.id ? 0 : Number(v || 0)), 0);
                    const remaining = Math.max(0, Number(paymentAmount || 0) - otherApplied);
                    next[invoice.id] = round2(Math.min(openBal, remaining) || 0);
                } else {
                    next[invoice.id] = round2(openBal);
                }
            } else {
                delete next[invoice.id];
            }
            return next;
        });
    };

    const onPaymentAmountChange = (v) => {
        setAmountTouched(true);
        setPaymentAmount(v);
    };

    // ---- submit -------------------------------------------------------------
    const validate = () => {
        if (!customerId) return 'Choose a customer first.';
        const pay = effectivePayment;
        if (!(pay > 0)) return 'Enter a payment amount (or apply it to at least one invoice).';
        for (const inv of invoices) {
            const amt = Number(applied[inv.id]);
            if (applied[inv.id] !== undefined && amt > Number(inv.open_balance) + 0.005) {
                return `Applied amount on invoice ${inv.invoice_number} exceeds its open balance (${money(inv.open_balance)}).`;
            }
        }
        if (appliedTotal - pay > 0.005) return 'The amount applied to invoices exceeds the payment amount.';
        return null;
    };

    const buildForm = () => {
        const fd = new FormData();
        fd.append('customer_id', customerId);
        fd.append('amount', round2(effectivePayment));
        fd.append('type', type);
        fd.append('payment_date', paymentDate.format('YYYY-MM-DD'));
        if (memo) fd.append('memo', memo);
        if (type === 'check' && checkNumber) fd.append('check_number', checkNumber);
        if (type === 'credit_card' && lastDigits) fd.append('last_digits', lastDigits);
        if ((type === 'ach' || type === 'wire') && referenceNumber) fd.append('reference_number', referenceNumber);
        if (file) fd.append('file', file);

        const applications = Object.entries(applied)
            .filter(([, v]) => Number(v) > 0)
            .map(([invoice_id, v]) => ({ invoice_id, amount: round2(v) }));
        if (applications.length) fd.append('applications', JSON.stringify(applications));
        return fd;
    };

    const clearForm = (keepNothing) => {
        setCustomerId(null); setCustomerName(''); setCustomerOptions([]);
        setInvoices([]); setApplied({});
        setPaymentAmount(null); setAmountTouched(false);
        setType('check'); setCheckNumber(''); setLastDigits(''); setReferenceNumber('');
        setPaymentDate(moment()); setMemo(''); setFile(null); setError(null);
    };

    const save = async (thenNew) => {
        const problem = validate();
        if (problem) { setError(problem); return; }
        setError(null);
        setSubmitting(true);
        try {
            const res = await api.post('/transactions', buildForm());
            if (!res || !res.success) {
                setError((res && res.errors) || 'Could not save the payment.');
                setSubmitting(false);
                return;
            }
            const count = (res.body && res.body.applications_count) || 0;
            message.success(
                `Payment of ${money(effectivePayment)} saved` +
                (count ? ` and applied to ${count} invoice${count === 1 ? '' : 's'}.` : '.'), 5);
            setSubmitting(false);
            if (thenNew) clearForm();
            else history.push('/transactions');
        } catch (e) {
            setError('Could not save the payment.');
            setSubmitting(false);
        }
    };

    // ---- render -------------------------------------------------------------
    const columns = [
        { title: 'Date', dataIndex: 'date', key: 'date', width: 110,
          render: (v) => v ? moment(v).format('MMM DD, YYYY') : '' },
        { title: 'Invoice #', dataIndex: 'invoice_number', key: 'invoice_number', width: 110,
          render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
        { title: 'Description', dataIndex: 'project_description', key: 'project_description' },
        { title: 'Site', dataIndex: 'site_name', key: 'site_name' },
        { title: 'Original', dataIndex: 'grand_total', key: 'grand_total', align: 'right', width: 110,
          render: (v) => money(v) },
        { title: 'Open Balance', dataIndex: 'open_balance', key: 'open_balance', align: 'right', width: 120,
          render: (v) => money(v) },
        { title: 'Payment', key: 'payment', align: 'right', width: 150,
          render: (_, record) => {
              const checked = applied[record.id] !== undefined;
              return <InputNumber
                  style={{ width: '130px' }}
                  min={0} step={0.01} precision={2}
                  disabled={!checked}
                  value={checked ? applied[record.id] : null}
                  formatter={(val) => `$ ${val}`}
                  parser={(val) => val.replace(/\$\s?|(,*)/g, '')}
                  onChange={(val) => setLineAmount(record.id, val)} />;
          } },
    ];

    const rowSelection = {
        selectedRowKeys: Object.keys(applied),
        onSelect: (record, selected) => toggleLine(record, selected),
        onSelectAll: (selected, _rows, changed) => changed.forEach((r) => toggleLine(r, selected)),
    };

    const label = { display: 'block', fontWeight: 600, margin: '0 0 4px', fontSize: '13px', color: '#555' };
    const field = { marginBottom: 16 };

    return <div style={{ margin: 'auto', maxWidth: '1200px', padding: '24px' }}>
        <h1 style={{ fontFamily: 'Raleway' }}>Receive Payment</h1>

        {error && <ErrorMessage errors={error} />}

        <Row gutter={24}>
            <Col span={16}>
                <Card style={{ marginBottom: 20 }}>
                    <Row gutter={16}>
                        <Col span={14}>
                            <div style={field}>
                                <span style={label}>Received From</span>
                                <Select
                                    showSearch allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Search customers…"
                                    value={customerId || undefined}
                                    filterOption={false}
                                    showArrow={false}
                                    notFoundContent={searching ? <Spin size="small" /> : null}
                                    onSearch={onCustomerSearch}
                                    onChange={selectCustomer}>
                                    {customerOptions.map((c) =>
                                        <Option key={c.id} value={c.id}>{c.name}</Option>)}
                                    {customerId && !customerOptions.find((c) => c.id === customerId) &&
                                        <Option key={customerId} value={customerId}>{customerName}</Option>}
                                </Select>
                            </div>
                        </Col>
                        <Col span={10}>
                            <div style={field}>
                                <span style={label}>Payment Date</span>
                                <DatePicker
                                    value={paymentDate} onChange={(d) => setPaymentDate(d || moment())}
                                    allowClear={false} format="MMM DD, YYYY" style={{ width: '100%' }} />
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <div style={field}>
                                <span style={label}>Payment Amount</span>
                                <InputNumber
                                    style={{ width: '100%' }} min={0} step={0.01} precision={2}
                                    value={amountTouched ? paymentAmount : appliedTotal}
                                    formatter={(v) => `$ ${v}`}
                                    parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
                                    onChange={onPaymentAmountChange} />
                            </div>
                        </Col>
                        <Col span={8}>
                            <div style={field}>
                                <span style={label}>Payment Type</span>
                                <Select style={{ width: '100%' }} value={type} onChange={setType}>
                                    {TYPE_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                                </Select>
                            </div>
                        </Col>
                        <Col span={8}>
                            {type === 'check' && <div style={field}>
                                <span style={label}>Check #</span>
                                <Input value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
                            </div>}
                            {type === 'credit_card' && <div style={field}>
                                <span style={label}>Last 4 CC Digits</span>
                                <Input value={lastDigits} maxLength={4}
                                    onChange={(e) => setLastDigits(e.target.value)} />
                            </div>}
                            {(type === 'ach' || type === 'wire') && <div style={field}>
                                <span style={label}>Confirmation #</span>
                                <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
                            </div>}
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={16}>
                            <div style={field}>
                                <span style={label}>Memo</span>
                                <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
                            </div>
                        </Col>
                        <Col span={8}>
                            <div style={field}>
                                <span style={label}>Attachment</span>
                                <Upload beforeUpload={(f) => { setFile(f); return false; }}
                                    onRemove={() => setFile(null)}
                                    fileList={file ? [file] : []}>
                                    <Button><Icon type="upload" /> Select File</Button>
                                </Upload>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </Col>

            <Col span={8}>
                <Card>
                    <Row gutter={8}>
                        <Col span={12}><Statistic title="Payment Amount" value={effectivePayment}
                            precision={2} prefix="$" /></Col>
                        <Col span={12}><Statistic title="Applied" value={appliedTotal}
                            precision={2} prefix="$" /></Col>
                    </Row>
                    <div style={{ marginTop: 16 }}>
                        <Statistic
                            title={unapplied < 0 ? 'Over-applied' : 'Unapplied (becomes credit)'}
                            value={Math.abs(unapplied)} precision={2} prefix="$"
                            valueStyle={{ color: unapplied < 0 ? '#cf1322' : (unapplied > 0 ? '#1890ff' : '#3f8600') }} />
                    </div>
                </Card>
            </Col>
        </Row>

        <Card
            title={customerId ? `Open Invoices — ${customerName}` : 'Open Invoices'}
            style={{ marginBottom: 20 }}>
            {loadingInvoices
                ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                : (!customerId
                    ? <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>
                        Choose a customer to see their open invoices.</div>
                    : (invoices.length === 0
                        ? <Alert type="info" showIcon
                            message="This customer has no open invoices. You can still record the payment as available credit." />
                        : <Table
                            rowKey="id"
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={invoices}
                            pagination={false}
                            size="middle" />))}
        </Card>

        <div style={{ display: 'flex', gap: 12 }}>
            <Button type="primary" icon="save" loading={submitting} onClick={() => save(false)}>
                Save &amp; Close
            </Button>
            <Button icon="plus" loading={submitting} onClick={() => save(true)}>
                Save &amp; New
            </Button>
            <Button onClick={() => clearForm()}>Clear</Button>
            <Button style={{ marginLeft: 'auto' }} onClick={() => history.push('/transactions')}>
                Cancel
            </Button>
        </div>
    </div>;
}

export default withRouter(ReceivePayment);
